import { useState, type FormEvent } from "react";
import { api } from "../../api/apiClient";
import type { MeResponse } from "../../api/types";

/**
 * G-136 — changing your own sign-in address; G-145 moved it behind the
 * pencil (`EditProfilePanel`). Confirming the CURRENT address lives in the
 * profile header instead (OD-224).
 *
 * A change needs the current password (a borrowed, signed-in phone must not
 * be enough to take the account), and applies only once the NEW address
 * confirms its link. The current address keeps working meanwhile and is told.
 */
export function ChangeEmailSection({
  me,
  onChanged,
}: {
  me: MeResponse;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");

  const requestChange = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    const target = newEmail.trim().toLowerCase();
    if (!target.includes("@")) {
      setError("Type the new address.");
      return;
    }
    setBusy(true);
    try {
      const { pendingEmail } = await api.requestMyEmailChange(target, currentPassword);
      setNotice(
        `We emailed ${pendingEmail}. Open the link there to make it your sign-in address — until then, keep using ${me.email}.`,
      );
      setChanging(false);
      setNewEmail("");
      setCurrentPassword("");
      await onChanged();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not request the change");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div role="group" className="profile-edit-section" aria-labelledby="profile-edit-email">
      <div className="profile-edit-row">
        <div className="profile-edit-row-text">
          <h3 id="profile-edit-email">Sign-in email</h3>
          <span className="muted profile-edit-value">{me.email}</span>
          {me.pendingEmail ? (
            <span className="muted profile-edit-value">Changing to {me.pendingEmail}</span>
          ) : null}
        </div>
        {!changing ? (
          <button type="button" className="button tinted" onClick={() => setChanging(true)}>
            Change email
          </button>
        ) : null}
      </div>

      {notice ? <div className="alert success">{notice}</div> : null}
      {error ? <div className="alert error">{error}</div> : null}

      {changing ? (
        <form className="form-grid" onSubmit={requestChange} noValidate>
          <div className="field">
            <label htmlFor="email-change-new">New email *</label>
            <input
              id="email-change-new"
              type="email"
              autoComplete="email"
              maxLength={254}
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="email-change-password">Current password *</label>
            <input
              id="email-change-password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>
          <div className="button-row full">
            <button
              type="submit"
              className="button"
              disabled={busy || currentPassword.length === 0}
            >
              {busy ? "Sending…" : "Send confirmation link"}
            </button>
            <button
              type="button"
              className="button plain"
              onClick={() => {
                setChanging(false);
                setError(null);
              }}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
